// ===========================================
// AI Chat Component
// ===========================================
// Floating chat interface for AI assistant

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { aiAPI, notesAPI } from '../services/api';
import { 
  X, 
  Send, 
  Loader, 
  Bot,
  User,
  Minimize2,
  Maximize2,
  Sparkles,
  ChevronDown,
  Bookmark,
  Check
} from 'lucide-react';
import './AIChat.scss';

const AIChat = () => {
  const { user } = useAuth();
  const { currentProject } = useProjects();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [savedId, setSavedId] = useState(null); // Track which message was just saved to notes
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Hey${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! 👋 I'm your DevPad AI assistant. I can help you with:

• **Project insights** - analyzing your projects and costs
• **Development tips** - best practices and suggestions
• **Quick actions** - managing services, env vars, milestones

${currentProject ? `I see you're working on **${currentProject.name}**. What would you like help with?` : 'What would you like help with today?'}`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length, user?.name, currentProject]);

  // ===========================================
  // Handle send message with conversation history
  // ===========================================
  // Sends last 10 messages as context for better AI responses
  const MAX_HISTORY_MESSAGES = 10;

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    // Update UI immediately
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      // Build conversation history for API (exclude welcome message, limit to last N)
      const conversationHistory = updatedMessages
        .filter(m => m.id !== 'welcome' && !m.isError)  // Skip welcome and error messages
        .slice(-MAX_HISTORY_MESSAGES)                   // Keep last N messages
        .slice(0, -1)                                   // Exclude current message (sent separately)
        .map(m => ({
          role: m.role,
          content: m.content
        }));

      const response = await aiAPI.chat({
        message: userMessage.content,
        projectId: currentProject?._id || currentProject?.id,
        conversationHistory
      });

      const aiMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.data.message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        isError: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ===========================================
  // Save AI Message to Dev Notes
  // ===========================================
  // Allows one-click saving of AI responses to the knowledge notebook
  const handleSaveToNotes = async (msg) => {
    try {
      // Auto-generate title from first 50 chars of content (strip markdown)
      const cleanContent = msg.content.replace(/\*\*/g, '').replace(/^• /gm, '');
      const title = cleanContent.substring(0, 50).trim() + 
        (cleanContent.length > 50 ? '...' : '');
      
      await notesAPI.create({
        title,
        content: msg.content,
        topic: 'ai',  // Default AI notes to 'ai' topic
        source: {
          type: 'ai_chat',
          aiMessageId: msg.id,
          projectId: currentProject?._id || currentProject?.id
        }
      });
      
      // Show saved feedback
      setSavedId(msg.id);
      setTimeout(() => setSavedId(null), 2000);
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  // Quick suggestions
  const suggestions = currentProject ? [
    'Analyze my project costs',
    'Suggest optimizations',
    'What should I work on next?'
  ] : [
    'Help me plan a new project',
    'Show me cost insights',
    'What can you help with?'
  ];

  // Handle suggestion click
  const handleSuggestion = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  // Render message content with basic markdown
  const renderContent = (content) => {
    // Convert **bold** to <strong>
    let html = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Convert • bullet points
    html = html.replace(/^• /gm, '<span class="bullet">•</span> ');
    // Convert newlines to <br>
    html = html.replace(/\n/g, '<br>');
    
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <div className="ai-chat">
      {/* Floating Button */}
      {!isOpen && (
        <button 
          className="ai-chat__trigger"
          onClick={() => setIsOpen(true)}
          aria-label="Open AI Assistant"
        >
          <Sparkles className="ai-chat__trigger-sparkle" size={14} />
          <Bot size={24} />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className={`ai-chat__panel ${isMinimized ? 'ai-chat__panel--minimized' : ''}`}>
          {/* Header */}
          <div className="ai-chat__header">
            <div className="ai-chat__header-info">
              <Bot size={20} />
              <span>DevPad AI</span>
              {currentProject && (
                <span className="ai-chat__context">
                  <ChevronDown size={12} />
                  {currentProject.name}
                </span>
              )}
            </div>
            <div className="ai-chat__header-actions">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="ai-chat__messages">
                {messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`ai-chat__message ai-chat__message--${msg.role} ${msg.isError ? 'ai-chat__message--error' : ''}`}
                  >
                    <div className="ai-chat__message-avatar">
                      {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
                    </div>
                    <div className="ai-chat__message-content">
                      {renderContent(msg.content)}
                    </div>
                    {/* Save to Notes button - only for AI responses (not welcome or error) */}
                    {msg.role === 'assistant' && msg.id !== 'welcome' && !msg.isError && (
                      <button
                        className={`ai-chat__save-btn ${savedId === msg.id ? 'saved' : ''}`}
                        onClick={() => handleSaveToNotes(msg)}
                        title="Save to Dev Notes"
                      >
                        {savedId === msg.id ? <Check size={12} /> : <Bookmark size={12} />}
                      </button>
                    )}
                  </div>
                ))}
                
                {loading && (
                  <div className="ai-chat__message ai-chat__message--assistant">
                    <div className="ai-chat__message-avatar">
                      <Bot size={16} />
                    </div>
                    <div className="ai-chat__message-content ai-chat__typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Suggestions */}
              {messages.length <= 1 && (
                <div className="ai-chat__suggestions">
                  {suggestions.map((suggestion, i) => (
                    <button 
                      key={i}
                      onClick={() => handleSuggestion(suggestion)}
                      className="ai-chat__suggestion"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="ai-chat__input-area">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  disabled={loading}
                  rows={1}
                />
                <button 
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="ai-chat__send"
                >
                  {loading ? <Loader size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIChat;

